import React, { FC, useState, useMemo, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { TypeAnimation } from 'react-type-animation';

import { ReactComponent as VerificationIcon } from 'assets/icons/Verification.svg';
import { ReactComponent as BullIcon } from 'assets/icons/Bull.svg';
import { ChatType, EVisibilityStatusOptions, UserObjectType } from 'constants/graphqlTypes';
import { COLORS } from 'styles/constants';

import Avatar from 'components/UI/Avatar';
import getAuthUser from 'helpers/getAuthUser';
import useDocumentTitle from 'helpers/useDocumentTitle';
import FollowButton from 'components/FollowButton';
import AvatarPlaceholder from 'components/UI/Avatar/AvatarPlaceholder';
import {
  TEST_ID_PROFILE_VIEW_NAME_LABEL,
  TEST_ID_PROFILE_VIEW_USERNAME_LABEL,
  TEST_PROFILE_ACTION_BTN,
} from 'constants/aqa/profile';

import { EUiButtonType } from 'components/UI/Button/constants';
import { useUserSettings } from 'graphQL/contextualIntro/hooks';
import { useCreateNewChat } from 'graphQL/messages/hooks';
import { ROUTE_MESSAGES_CHAT } from 'routes';
import { transformRoute } from 'helpers/routingHelper';
import { AVATAR_DIMENSION } from './constants';
import { generateFullName } from './helpers';
import ConnectionsButton from './ConnectionsButton';
import SlotReelNumber from './SlotReelNumber';

import {
  StyledFollowButtonContainer,
  StyledFullName,
  StyledInfoContainer,
  StyledMessageButton,
  StyledTabsContainer,
  StyledTab,
  StyledText,
  StyledUserInfo,
  StyledVerifiedIcon,
  StyledGrain,
  StyledFilterToggleContainer,
} from './styled';
import ExpandableText from './ExpandableText';

interface IInfoProps {
  noContent: boolean;
  userData?: UserObjectType;
  fontColor: string;
  hideGradient: boolean;
  onTabChange?: (tabIndex: number) => void;
  activeTabIndex?: number;
  cardsCount: number;
  sparksCount: number;
  listsCount: number;
  gradientStart?: string | null;
  gradientEnd?: string | null;
  isDark?: boolean;
  showGradient?: boolean;
  showStyledGrain?: boolean;
  filterToggle?: React.ReactNode;
  hasSparkCategories?: boolean;
}

interface ITab {
  label: string | React.ReactElement;
}

interface ITabsContainerProps {
  tabs: ITab[];
  onClick: (index: number) => void;
  currentTabIndex: number;
  shouldAnimate: boolean;
  isTransitioning: boolean;
  previousTabIndex: number | null;
  hasCompletedInitialAnimation: boolean;
}

const TabsContainer: FC<ITabsContainerProps> = ({
  tabs,
  onClick,
  currentTabIndex,
  shouldAnimate,
  isTransitioning,
  previousTabIndex,
  hasCompletedInitialAnimation,
}) => {
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;

    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const deltaX = touchEndX - touchStartX.current;
    const deltaY = touchEndY - touchStartY.current;

    // Only trigger swipe if horizontal movement is greater than vertical (to avoid interfering with scroll)
    // and if the swipe distance is significant enough (minimum 50px)
    const minSwipeDistance = 50;
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
      if (deltaX < 0 && currentTabIndex < tabs.length - 1) {
        // Swipe left -> go to next tab
        onClick(currentTabIndex + 1);
      } else if (deltaX > 0 && currentTabIndex > 0) {
        // Swipe right -> go to previous tab
        onClick(currentTabIndex - 1);
      }
    }

    touchStartX.current = null;
    touchStartY.current = null;
  };

  useEffect(() => {
    if (tabsContainerRef.current && tabRefs.current.length > 0) {
      const activeTab = tabRefs.current[currentTabIndex];
      const previousTab = previousTabIndex !== null ? tabRefs.current[previousTabIndex] : null;

      let resizeObserver: ResizeObserver | null = null;
      let remeasureTimer: ReturnType<typeof setTimeout> | null = null;

      if (activeTab) {
        const labelElement = activeTab.querySelector('.tab-label') as HTMLElement;
        if (labelElement) {
          // Measure width immediately and set it synchronously (Safari mobile fix)
          const measureWidth = () => {
            const width = labelElement.offsetWidth;
            activeTab.style.setProperty('--tab-label-width', `${width}px`);
            return width;
          };

          // Set width immediately for Safari mobile - don't wait for RAF
          measureWidth();

          // Also measure on next frame to catch any layout changes
          requestAnimationFrame(() => {
            measureWidth();
          });

          // Use ResizeObserver to track width changes during slot reel animation
          resizeObserver = new ResizeObserver((entries) => {
            entries.forEach((entry) => {
              const { width } = entry.contentRect;
              activeTab.style.setProperty('--tab-label-width', `${width}px`);
            });
          });

          resizeObserver.observe(labelElement);

          // Also re-measure after a delay to account for slot reel animation completion
          // Slot reel: max delay is (digitCount - 1) * 100ms + 800ms duration
          // For safety, measure again after 1500ms
          remeasureTimer = setTimeout(() => {
            measureWidth();
          }, 1500);
        }
      }

      if (isTransitioning && previousTab && activeTab) {
        const labelElement = activeTab.querySelector('.tab-label') as HTMLElement;
        if (labelElement) {
          // Measure width immediately and set it synchronously before animation starts (Safari mobile fix)
          const width = labelElement.offsetWidth;
          activeTab.style.setProperty('--tab-label-width', `${width}px`);
          activeTab.style.setProperty('--rect-offset', `calc(-${width}px / 2)`);
        }

        // Use requestAnimationFrame to ensure layout is complete before measuring positions
        requestAnimationFrame(() => {
          const previousRect = previousTab.getBoundingClientRect();
          const activeRect = activeTab.getBoundingClientRect();

          // Calculate the distance to move from previous tab center to active tab center
          const moveFrom = previousRect.left + previousRect.width / 2 - (activeRect.left + activeRect.width / 2);
          const moveTo = 0;

          activeTab.style.setProperty('--move-from', `${moveFrom}px`);
          activeTab.style.setProperty('--move-to', `${moveTo}px`);

          // Remeasure width on first frame to ensure it's correct
          if (labelElement) {
            const width = labelElement.offsetWidth;
            activeTab.style.setProperty('--tab-label-width', `${width}px`);
            // Calculate offset to center the rectangle: -50% of the rectangle width
            // Since we're using left: 50%, we need to offset by -width/2 to center it
            activeTab.style.setProperty('--rect-offset', `calc(-${width}px / 2)`);
          }
        });
      }

      return () => {
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        if (remeasureTimer) {
          clearTimeout(remeasureTimer);
        }
      };
    }
    return undefined;
  }, [currentTabIndex, shouldAnimate, isTransitioning, previousTabIndex, tabs]);

  return (
    <StyledTabsContainer ref={tabsContainerRef} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      {tabs.map((tab, index) => (
        <StyledTab
          ref={(el) => {
            tabRefs.current[index] = el;
          }}
          key={`tab-${tab.label}`}
          onClick={() => onClick(index)}
          active={index === currentTabIndex}
          isTransitioning={isTransitioning && index === currentTabIndex}
          wasActive={previousTabIndex === index && index !== currentTabIndex}
          shouldAnimate={shouldAnimate && index === currentTabIndex && index === 0 && !hasCompletedInitialAnimation}
          hideInitially={index === 0 && index === currentTabIndex && !hasCompletedInitialAnimation}
        >
          <div className="tab-content-wrapper">
            <span className="tab-label">{tab.label}</span>
            {index === currentTabIndex ? (
              <span className="bull-icon-container">
                <BullIcon />
              </span>
            ) : (
              <span className="bull-icon-placeholder">&nbsp;</span>
            )}
          </div>
        </StyledTab>
      ))}
    </StyledTabsContainer>
  );
};

const Info: FC<IInfoProps> = (props) => {
  const {
    noContent,
    userData,
    fontColor,
    hideGradient,
    onTabChange,
    activeTabIndex: externalTabIndex,
    cardsCount,
    sparksCount,
    listsCount,
    gradientStart,
    gradientEnd,
    isDark,
    showStyledGrain,
    filterToggle,
    hasSparkCategories = true,
  } = props;
  const { username, userInfo, id: userId, isBlockedByMe, isBlockedMe, isFollowingMe } = userData ?? {};
  const { firstName, lastName, bio, bioLong } = userInfo ?? {};
  const { userId: currentUserId } = getAuthUser();
  const { push } = useHistory();

  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hasCompletedInitialAnimation, setHasCompletedInitialAnimation] = useState(false);
  const previousTabIndexRef = useRef<number | null>(null);
  const mountTimeRef = useRef<number>(Date.now());

  const allReady = cardsCount !== 0 || sparksCount !== 0 || listsCount !== 0;

  // Sync with external activeTabIndex (from swipe gestures in ProfileTop)
  useEffect(() => {
    if (externalTabIndex !== undefined && externalTabIndex !== currentTabIndex) {
      // Use the same transition logic as handleTabChange
      if (!hasCompletedInitialAnimation && externalTabIndex === 0) {
        setCurrentTabIndex(externalTabIndex);
        return;
      }

      previousTabIndexRef.current = currentTabIndex;
      setIsTransitioning(true);

      setTimeout(() => {
        setIsTransitioning(false);
        previousTabIndexRef.current = null;
      }, 1400);

      setCurrentTabIndex(externalTabIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalTabIndex]);

  // Trigger animation 2 seconds after page starts loading (on Picks tab) - only once
  useEffect(() => {
    if (hasCompletedInitialAnimation) {
      return undefined;
    }

    const elapsed = Date.now() - mountTimeRef.current;
    const delay = Math.max(0, 2000 - elapsed);

    const timer = setTimeout(() => {
      if (currentTabIndex === 0 && !shouldAnimate) {
        setShouldAnimate(true);
        // Mark initial animation as completed after it finishes (1s animation + small buffer)
        setTimeout(() => {
          setHasCompletedInitialAnimation(true);
          setShouldAnimate(false);
        }, 1100);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [currentTabIndex, shouldAnimate, hasCompletedInitialAnimation]);

  const tabs = useMemo(() => {
    const picksTab = {
      label: (
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          {(() => {
            const digits = cardsCount.toString().split('');
            if (!allReady) {
              return (
                <span style={{ display: 'inline' }}>
                  {digits.map((d, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <span key={i}>{d}</span>
                  ))}
                </span>
              );
            }
            return (
              <span style={{ display: 'inline' }}>
                {digits.map((d, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <span key={i}>
                    <SlotReelNumber value={parseInt(d, 10)} delay={i * 100} />
                  </span>
                ))}
              </span>
            );
          })()}
          &nbsp;<span>Picks</span>
        </div>
      ),
    };

    const sparksTab = {
      label: (
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          {(() => {
            const digits = sparksCount.toString().split('');
            if (!allReady) {
              return (
                <span style={{ display: 'inline' }}>
                  {digits.map((d, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <span key={i}>{d}</span>
                  ))}
                </span>
              );
            }
            return (
              <span style={{ display: 'inline' }}>
                {digits.map((d, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <span key={i}>
                    <SlotReelNumber value={parseInt(d, 10)} delay={i * 100} />
                  </span>
                ))}
              </span>
            );
          })()}
          &nbsp;<span>Sparks</span>
        </div>
      ),
    };

    const listsTab = {
      label: (
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          {(() => {
            const digits = listsCount.toString().split('');
            if (!allReady) {
              return (
                <span style={{ display: 'inline' }}>
                  {digits.map((d, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <span key={i}>{d}</span>
                  ))}
                </span>
              );
            }
            return (
              <span style={{ display: 'inline' }}>
                {digits.map((d, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <span key={i}>
                    <SlotReelNumber value={parseInt(d, 10)} delay={i * 100} />
                  </span>
                ))}
              </span>
            );
          })()}
          &nbsp;<span>Lists</span>
        </div>
      ),
    };

    // Only include Sparks tab if spark categories exist
    return hasSparkCategories ? [picksTab, sparksTab, listsTab] : [picksTab, listsTab];
  }, [cardsCount, sparksCount, listsCount, allReady, hasSparkCategories]);

  const handleTabChange = (index: number) => {
    if (index === currentTabIndex) return;

    // Always use transition animation when switching tabs (after initial animation completes)
    // If initial animation hasn't completed yet and switching to tab 0, just switch without transition
    if (!hasCompletedInitialAnimation && index === 0) {
      setCurrentTabIndex(index);
      if (onTabChange) {
        onTabChange(index);
      }
      return;
    }

    previousTabIndexRef.current = currentTabIndex;
    setIsTransitioning(true);

    // After transition completes, reset state
    setTimeout(() => {
      setIsTransitioning(false);
      previousTabIndexRef.current = null;
    }, 1400); // 800ms movement + 600ms return to circle

    setCurrentTabIndex(index);
    if (onTabChange) {
      onTabChange(index);
    }
  };

  useDocumentTitle(generateFullName(userData));
  const { messagesAndThoughtsVisibility } = useUserSettings(userId);
  const isAllowedToMessage =
    messagesAndThoughtsVisibility === EVisibilityStatusOptions.Everyone ||
    (messagesAndThoughtsVisibility === EVisibilityStatusOptions.Following && isFollowingMe);

  const { createChat } = useCreateNewChat((chatId: string) =>
    push(transformRoute(ROUTE_MESSAGES_CHAT, { id: chatId }))
  );

  const [isCreateThreadDisabled, setIsCreateThreadDisabled] = useState(false);

  const handleCreateThread = () => {
    if (userId) {
      createChat([userId], ChatType.Single);
      setIsCreateThreadDisabled(true);
    }
  };

  return (
    <StyledInfoContainer
      hasWhiteAvatar={!noContent && !hideGradient}
      hasPaddingBottom={noContent}
      showGradient={false}
      gradientStart={gradientStart}
      gradientEnd={gradientEnd}
      isDark={isDark}
    >
      {showStyledGrain && <StyledGrain />}
      {userData ? (
        <Avatar user={userData} dimension={AVATAR_DIMENSION} isAnimated />
      ) : (
        <AvatarPlaceholder dimension={AVATAR_DIMENSION} />
      )}
      <StyledFullName key={`${firstName}${lastName}`} data-testid={TEST_ID_PROFILE_VIEW_USERNAME_LABEL}>
        {userData ? (
          <TypeAnimation wrapper="span" cursor={false} sequence={[`${firstName} ${lastName}`, 100]} speed={35} />
        ) : (
          'Sorry, this profile cannot be found'
        )}
      </StyledFullName>
      <StyledUserInfo>
        {userData && (
          <StyledText data-testid={TEST_ID_PROFILE_VIEW_NAME_LABEL}>
            :{username}
            <StyledVerifiedIcon>
              <VerificationIcon />
            </StyledVerifiedIcon>
          </StyledText>
        )}
        {bio && <StyledText shouldBreak>{bio}</StyledText>}
        {bioLong && <ExpandableText text={bioLong} shouldBreak />}
        {username && (
          <ConnectionsButton username={username} connectionsCount={userData?.userCounters?.connectionsCount ?? 0} />
        )}
      </StyledUserInfo>
      {userId !== currentUserId && username && userData && (
        <StyledFollowButtonContainer>
          <FollowButton dataTestId={TEST_PROFILE_ACTION_BTN} user={userData} isDark={fontColor === COLORS.white[100]} />
          {!isBlockedMe && !isBlockedByMe && isAllowedToMessage && (
            <StyledMessageButton
              onClick={handleCreateThread}
              disabled={isCreateThreadDisabled}
              buttonType={EUiButtonType.Transparent}
              isDark={fontColor === COLORS.white[100]}
            >
              Message
            </StyledMessageButton>
          )}
        </StyledFollowButtonContainer>
      )}
      <TabsContainer
        tabs={tabs}
        onClick={handleTabChange}
        currentTabIndex={currentTabIndex}
        shouldAnimate={shouldAnimate}
        isTransitioning={isTransitioning}
        previousTabIndex={previousTabIndexRef.current}
        hasCompletedInitialAnimation={hasCompletedInitialAnimation}
      />
      {filterToggle && <StyledFilterToggleContainer>{filterToggle}</StyledFilterToggleContainer>}
    </StyledInfoContainer>
  );
};

export default Info;
